import { formatBrainActionPrompt } from "./brainActions.ts";
import type { BrainComposerAttachment } from "./brainComposerCollection.ts";
import { splitLeadingComposerSkillLines } from "./composerSkillToken.ts";

/**
 * Project References: the project-root folder that holds reference material
 * whose bytes had to travel to the Environment because they existed only in
 * the client. A reference the Environment host can already read is named by
 * its own path and never copied here. The App never commits this folder.
 */
export const PROJECT_REFERENCES_DIRECTORY = "references";

/**
 * What the ingest thread a new project opens on is called until the first
 * turn's generated title replaces it.
 */
export const PROJECT_REFERENCES_INGEST_THREAD_TITLE = "Ingest references";

/** A new project is named after the folder the user created it in. */
export function projectTitleFromDestination(destinationPath: string): string {
  const segments = destinationPath
    .trim()
    .split(/[\\/]+/)
    .filter((segment) => segment.length > 0);
  return segments.at(-1) ?? destinationPath.trim();
}

/**
 * The name a reference takes inside Project References, never outside it and
 * never overwriting one already there. Names compare case-insensitively because the folder may live on
 * a filesystem that does, and a collision there would silently replace a file.
 */
export function projectReferenceTargetName(rawName: string, taken: ReadonlySet<string>): string {
  // The name arrives from a client: only its last path segment is used, so a
  // reference can never land outside Project References.
  const lastSegment =
    rawName
      .split(/[\\/]+/)
      .at(-1)
      ?.trim() ?? "";
  const name =
    lastSegment === "" || lastSegment === "." || lastSegment === ".." ? "reference" : lastSegment;
  const takenLower = new Set([...taken].map((entry) => entry.toLowerCase()));
  if (!takenLower.has(name.toLowerCase())) return name;
  const extensionStart = name.lastIndexOf(".");
  const [stem, extension] =
    extensionStart > 0 ? [name.slice(0, extensionStart), name.slice(extensionStart)] : [name, ""];
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${stem}-${suffix}${extension}`;
    if (!takenLower.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * The visible first message of the ingest thread a new project opens on: an
 * ordinary Ingest Action over every reference, exactly as the composer would
 * arrange it, so creation and a later ingest read the same way.
 */
export function formatProjectReferencesIngestPrompt(
  references: ReadonlyArray<BrainComposerAttachment>,
): string {
  return formatBrainActionPrompt({
    action: "ingest",
    collection: { attachments: references, textSources: [], deleteTargets: [] },
    text: "",
  });
}

/**
 * Whether a prompt is an Ingest send: its leading skill chips include the
 * ingest Skill Bundle's. Messages sent before Actions carried only the chip, so
 * the prompt rather than the message's Action is what this reads.
 */
export function isBrainIngestPrompt(text: string | undefined): boolean {
  if (text === undefined) return false;
  return splitLeadingComposerSkillLines(text.trimStart()).skillNames.includes("ingest");
}
